/* Анимации звука «Кружка из аудио» (APP-SPEC-4 §6.1, drawViz прототипа): оформление рамки и карточек ленты, не данные.
   Уровни считаются от времени; живёт только главная рамка (live), карточки — один кадр; при «уменьшить движение» — один кадр у всех. */
import { useEffect, useRef } from 'react';
import { reduced } from '../../../ui/motion';

const lvl = (i: number, t: number) => 0.25 + 0.75 * Math.abs(Math.sin(t * 2.1 + i * 0.63) * Math.sin(t * 0.7 + i * 1.7));
const blobR = (th: number, R: number, a: number, t: number, ph: number) =>
  R * (1 + a * (0.55 * Math.sin(3 * th + t * 1.1 + ph) + 0.3 * Math.sin(5 * th - t * 1.7 + ph * 2) + 0.15 * Math.sin(8 * th + t * 2.3)));

/** Кадр анимации k на холсте любой формы: W×H, R — меньшая сторона. */
function drawCircleViz(cv: HTMLCanvasElement, t: number, k: string) {
  const dpr = Math.min(2, window.devicePixelRatio || 1),
    W = cv.clientWidth || 196,
    H = cv.clientHeight || W,
    R = Math.min(W, H);
  if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) {
    cv.width = Math.round(W * dpr);
    cv.height = Math.round(H * dpr);
  }
  const x = cv.getContext?.('2d');
  if (!x) return;
  x.setTransform(dpr, 0, 0, dpr, 0, 0);
  const cx = W / 2,
    cy = H / 2,
    amp = 0.55 + 0.45 * Math.sin(t * 1.3);
  x.clearRect(0, 0, W, H);
  x.globalCompositeOperation = 'source-over';
  x.shadowBlur = 0;
  const disc = (px: number, py: number, r: number) => {
    x.beginPath();
    x.arc(px, py, r, 0, 7);
  };
  if (k === 'orb') {
    const g = x.createRadialGradient(cx, cy, 0, cx, cy, R * 0.7);
    g.addColorStop(0, '#101424');
    g.addColorStop(1, '#05060c');
    x.fillStyle = g;
    x.fillRect(0, 0, W, H);
    x.globalCompositeOperation = 'lighter';
    const cols = ['rgba(140,170,255,', 'rgba(255,255,255,', 'rgba(120,230,255,', 'rgba(200,160,255,'];
    for (let i = 0; i < 4; i++) {
      const a = t * (0.6 + i * 0.17) + i * 1.6,
        r = R * (0.16 + 0.06 * Math.sin(t * 1.9 + i)),
        d = R * (0.1 + 0.07 * amp),
        px = cx + Math.cos(a) * d,
        py = cy + Math.sin(a * 1.3) * d;
      const gg = x.createRadialGradient(px, py, 0, px, py, r * 1.9);
      gg.addColorStop(0, cols[i] + '.95)');
      gg.addColorStop(0.45, cols[i] + '.35)');
      gg.addColorStop(1, cols[i] + '0)');
      x.fillStyle = gg;
      disc(px, py, r * 1.9);
      x.fill();
    }
    x.globalCompositeOperation = 'source-over';
    return;
  }
  if (k === 'morph') {
    const g = x.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#0b0b10');
    g.addColorStop(1, '#15121f');
    x.fillStyle = g;
    x.fillRect(0, 0, W, H);
    const R0 = R * (0.27 + 0.05 * amp),
      gr = x.createLinearGradient(cx - R0, cy - R0, cx + R0, cy + R0);
    gr.addColorStop(0, '#7c8cff');
    gr.addColorStop(0.5, '#f06bd0');
    gr.addColorStop(1, '#5de0ff');
    x.shadowColor = 'rgba(160,120,255,.55)';
    x.shadowBlur = 26;
    x.fillStyle = gr;
    x.beginPath();
    for (let i = 0; i <= 120; i++) {
      const th = (i / 120) * Math.PI * 2,
        r = blobR(th, R0, 0.14 + 0.1 * amp, t, 0);
      x.lineTo(cx + Math.cos(th) * r, cy + Math.sin(th) * r);
    }
    x.closePath();
    x.fill();
    x.shadowBlur = 0;
    return;
  }
  if (k === 'ferro') {
    const bg = x.createRadialGradient(cx, cy * 0.5, 0, cx, cy, R * 0.85);
    bg.addColorStop(0, '#f6f6f8');
    bg.addColorStop(0.7, '#d9dae0');
    bg.addColorStop(1, '#bdbfc8');
    x.fillStyle = bg;
    x.fillRect(0, 0, W, H);
    const sp = 0.18 + 0.34 * amp;
    const drops: Array<[number, number, number, number]> = [
      [cx, cy, R * 0.2, 0],
      [cx + Math.cos(t * 0.5) * R * 0.2, cy + Math.sin(t * 0.5) * R * 0.14, R * 0.085, 2.1],
      [cx - Math.cos(t * 0.37 + 1) * R * 0.19, cy - Math.sin(t * 0.37 + 1) * R * 0.15, R * 0.065, 4.2],
    ];
    x.beginPath();
    for (const [dx, dy, r0, ph] of drops) {
      x.moveTo(dx + r0, dy);
      for (let i = 0; i <= 200; i++) {
        const th = (i / 200) * Math.PI * 2,
          s1 = Math.max(0, Math.sin(16 * th + ph + t * 0.8)),
          s2 = Math.max(0, Math.sin(9 * th - ph - t * 1.2)),
          r = r0 * (1 + sp * (Math.pow(s1, 12) + 0.7 * Math.pow(s2, 16)) + 0.04 * Math.sin(3 * th + t * 1.6));
        x.lineTo(dx + Math.cos(th) * r, dy + Math.sin(th) * r);
      }
      x.closePath();
    }
    x.shadowColor = 'rgba(0,0,0,.4)';
    x.shadowBlur = 16;
    x.fillStyle = '#07070a';
    x.fill();
    x.shadowBlur = 0;
    return;
  }
  if (k === 'pulse') {
    x.fillStyle = '#0c0c0f';
    x.fillRect(0, 0, W, H);
    for (let i = 0; i < 5; i++) {
      const p = (t * 0.45 + i / 5) % 1,
        r = R * 0.08 + p * R * 0.45,
        a = (1 - p) * (0.55 + 0.45 * amp);
      x.strokeStyle = 'rgba(255,255,255,' + a.toFixed(3) + ')';
      x.lineWidth = 1.5 + (1 - p) * 2.5;
      disc(cx, cy, r);
      x.stroke();
    }
    x.fillStyle = '#fff';
    disc(cx, cy, R * (0.07 + 0.03 * amp));
    x.fill();
    return;
  }
  if (k === 'retro') {
    x.fillStyle = '#03150a';
    x.fillRect(0, 0, W, H);
    x.strokeStyle = 'rgba(54,255,122,.12)';
    x.lineWidth = 1;
    const st = R / 8;
    for (let g = 0; g < W; g += st) {
      x.beginPath();
      x.moveTo(g, 0);
      x.lineTo(g, H);
      x.stroke();
    }
    for (let g = 0; g < H; g += st) {
      x.beginPath();
      x.moveTo(0, g);
      x.lineTo(W, g);
      x.stroke();
    }
    x.strokeStyle = '#36ff7a';
    x.lineWidth = 2;
    x.shadowColor = '#36ff7a';
    x.shadowBlur = 10;
    x.beginPath();
    for (let i = 0; i <= W; i += 2) {
      const a = lvl(i / 9, t) * 0.42;
      x.lineTo(i, cy + Math.sin((i / W) * Math.PI * 6 + t * 5) * a * R * 0.5 * Math.sin((i / W) * Math.PI));
    }
    x.stroke();
    x.shadowBlur = 0;
    return;
  }
  if (k === 'matrix') {
    x.fillStyle = '#020403';
    x.fillRect(0, 0, W, H);
    const cols = 15,
      cw = W / cols,
      len = 12,
      span = H + len * cw;
    x.font = `600 ${Math.round(cw * 0.78)}px ui-monospace,monospace`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    for (let c = 0; c < cols; c++) {
      const seed = (c * 7919) % 97,
        sp = (0.55 + (seed / 97) * 0.9) * cw * 2.6,
        head = (t * sp + seed * 31) % span;
      for (let r = 0; r < len; r++) {
        const y = head - r * cw;
        if (y < -cw || y > H + cw) continue;
        const ch = String.fromCharCode(0x30a1 + ((c * 13 + r * 7 + Math.floor(t * (r === 0 ? 6 : 1.5) + c)) % 84));
        x.fillStyle = r === 0 ? 'rgba(225,255,236,.95)' : `rgba(54,230,110,${Math.pow(1 - r / len, 1.7).toFixed(3)})`;
        x.fillText(ch, c * cw + cw / 2, y);
      }
    }
    return;
  }
  if (k === 'ring') {
    x.fillStyle = '#050507';
    x.fillRect(0, 0, W, H);
    const N = 9,
      kk = 0.78,
      base0 = R * 0.47;
    for (let i = N - 1; i >= 0; i--) {
      const ph = (i + t * 0.35) % N,
        r = base0 * Math.pow(kk, ph),
        al = Math.max(0, 1 - (ph / N) * 1.05),
        n = Math.round(10 + (r / base0) * 34);
      for (let j = 0; j < n; j++) {
        const a = (j / n) * Math.PI * 2 + ph * 0.15,
          b = al * (0.45 + 0.55 * Math.pow(Math.abs(Math.sin(a * 3 + t * 2.5)), 2) * amp);
        x.fillStyle = `rgba(190,225,255,${b.toFixed(3)})`;
        disc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, Math.max(1, 2.6 * (1 - (ph / N) * 0.7)));
        x.fill();
      }
    }
    return;
  }
  if (k === 'bars') {
    x.fillStyle = '#0d0d10';
    x.fillRect(0, 0, W, H);
    const n = 13,
      gap = W / (n * 2 + 1);
    x.fillStyle = '#fff';
    for (let i = 0; i < n; i++) {
      const h = Math.max(4, lvl(i, t) * R * 0.5);
      const bx = gap + i * gap * 2;
      x.beginPath();
      x.roundRect?.(bx, cy - h / 2, gap, h, gap / 2);
      if (!x.roundRect) x.rect(bx, cy - h / 2, gap, h);
      x.fill();
    }
    return;
  }
  /* wave и неизвестные значения: ленты синусоид и яркая линия */
  const g = x.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#15151a');
  g.addColorStop(1, '#09090b');
  x.fillStyle = g;
  x.fillRect(0, 0, W, H);
  const A = 0.12 + 0.16 * amp;
  const line = (ph: number, f1: number, f2: number, a: number, alpha: number) => {
    x.beginPath();
    for (let i = 0; i <= W; i += 2) {
      const u = i / W,
        env = Math.sin(u * Math.PI),
        y = cy + (Math.sin(u * Math.PI * f1 + t * 1.6 + ph) * 0.6 + Math.sin(u * Math.PI * f2 - t * 2.3 + ph * 1.7) * 0.4) * a * env * R;
      if (i === 0) x.moveTo(i, y);
      else x.lineTo(i, y);
    }
    x.strokeStyle = `rgba(255,255,255,${alpha})`;
    x.lineWidth = 2;
    x.stroke();
  };
  line(0, 2.2, 3.7, A * 1.15, 0.25);
  line(1.3, 2.8, 4.6, A * 0.85, 0.35);
  line(0.6, 2.4, 3.9, A * 0.9, 0.95);
}

/** Холст анимации: live — каждый кадр (кроме «уменьшить движение»), иначе один кадр. */
export function CircleViz({ style, live = false, className = '' }: { style: string; live?: boolean; className?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    let frame = 0;
    const still = !live || reduced();
    const tick = () => {
      drawCircleViz(element, still ? 1.7 : performance.now() / 1000, style);
      if (!still) frame = requestAnimationFrame(tick);
    };
    try {
      tick();
    } catch {
      /* холст недоступен (тестовая среда): рамка остаётся пустой */
    }
    return () => cancelAnimationFrame(frame);
  }, [style, live]);
  return (
    <span className={`face viz${className ? ' ' + className : ''}`}>
      <canvas ref={canvas} data-viz={style} aria-hidden="true" />
    </span>
  );
}
